import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { client, newIdempotencyKey } from '../lib/api'
import { mediaUrl } from '../lib/apiBase'
import { Avatar } from '../lib/ui'
import { ReportBlock } from './ReportBlock'
import { PhotoRequestPanel } from './PhotoRequestPanel'
import { strikeWarningCopy, type MessageStrikeInfo } from '../../shared/message-moderation'
import {
  appendUniqueMessage,
  formatMessageTime,
  mergeChatMessages,
  INBOX_POLL_MS,
  THREAD_POLL_MS,
} from '../lib/chat-utils'
import type { ConversationSummary, MessageQuote } from '../../shared/types'

type ChatMessage = {
  id: string
  sender_id: string
  body: string
  created_at: string
  hasAttachment?: boolean
  attachmentUrl?: string | null
  readByPeer?: boolean
  blocked?: boolean
  blockReason?: string | null
  blockCategory?: string | null
}

function formatSuspensionCountdown(until: string): string {
  const ms = new Date(until).getTime() - Date.now()
  if (ms <= 0) return '0m'
  const hours = Math.floor(ms / 3_600_000)
  const mins = Math.floor((ms % 3_600_000) / 60_000)
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

type ConversationMeta = {
  kind: string
  isPaidIntro: boolean
  peerLastReadAt: string | null
  stakeStatus: string | null
  canDeclineIntro: boolean
  readReceiptsEnabled: boolean
}

type InboxTab = 'matches' | 'requests'

export type ChatDraft = {
  peerId: string
  peerName: string
  color: string
}

export function Messages({
  meId,
  draft,
  clearDraft,
  notify,
  onThreadActive,
}: {
  meId: string
  draft: ChatDraft | null
  clearDraft: () => void
  notify: (msg: string) => void
  onThreadActive?: (active: boolean) => void
}) {
  const [inbox, setInbox] = useState<InboxTab>('matches')
  const [conversations, setConversations] = useState<ConversationSummary[] | null>(null)
  const [selected, setSelected] = useState<ConversationSummary | null>(null)
  const [pendingPeer, setPendingPeer] = useState<ChatDraft | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [convMeta, setConvMeta] = useState<ConversationMeta | null>(null)
  const [text, setText] = useState('')
  const [attachment, setAttachment] = useState<File | null>(null)
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null)
  const [quote, setQuote] = useState<MessageQuote | null>(null)
  const [confirmPay, setConfirmPay] = useState<{ credits: number } | null>(null)
  const [sending, setSending] = useState(false)
  const [loadingThread, setLoadingThread] = useState(false)
  const [chatMenu, setChatMenu] = useState(false)
  const [threadBusy, setThreadBusy] = useState(false)
  const [moderation, setModeration] = useState<MessageStrikeInfo | null>(null)
  const [countdownNow, setCountdownNow] = useState(Date.now())
  const [mailQuery, setMailQuery] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadList = useCallback(
    async (tab: InboxTab = inbox) => {
      try {
        const r = await client.conversations(tab)
        setConversations(r.conversations)
        return r.conversations
      } catch (e) {
        notify((e as Error).message)
        setConversations([])
        return []
      }
    },
    [inbox, notify],
  )

  const loadThread = useCallback(async (conversationId: string, quiet = false) => {
    if (!quiet) setLoadingThread(true)
    try {
      const r = await client.messages(conversationId)
      setMessages((prev) => (quiet ? mergeChatMessages(prev, r.messages) : r.messages))
      setConvMeta(r.conversation ?? null)
      setModeration(r.moderation ?? null)
      return r
    } catch (e) {
      if (!quiet) notify((e as Error).message)
      return null
    } finally {
      if (!quiet) setLoadingThread(false)
    }
  }, [notify])

  useEffect(() => {
    void loadList(inbox)
  }, [inbox, loadList])

  useEffect(() => {
    setMailQuery('')
    setUnreadOnly(false)
  }, [inbox])

  const filteredConversations = useMemo(() => {
    if (!conversations) return []
    const q = mailQuery.trim().toLowerCase()
    return conversations.filter((c) => {
      if (unreadOnly && c.unread <= 0) return false
      if (!q) return true
      return (
        c.peerName.toLowerCase().includes(q) ||
        c.preview.toLowerCase().includes(q) ||
        (c.peerCity?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [conversations, mailQuery, unreadOnly])

  useEffect(() => {
    if (!draft) return
    void (async () => {
      const list = await loadList('matches')
      const requestList = await client.conversations('requests').then((r) => r.conversations).catch(() => [])
      const existing = [...list, ...requestList].find((c) => c.peerId === draft.peerId)
      if (existing) {
        setInbox(existing.inbox)
        await openConversation(existing)
      } else {
        setSelected(null)
        setConvMeta(null)
        setPendingPeer(draft)
        setMessages([])
        try {
          const q = await client.quote(draft.peerId)
          setQuote(q.quote)
        } catch {
          setQuote(null)
        }
      }
      clearDraft()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft])

  const openConversation = async (c: ConversationSummary) => {
    setPendingPeer(null)
    setSelected(c)
    setQuote(null)
    setChatMenu(false)
    setConvMeta(null)
    await loadThread(c.id)
    setConversations((list) => (list ? list.map((x) => (x.id === c.id ? { ...x, unread: 0 } : x)) : list))
  }

  useEffect(() => {
    if (!selected) return
    const id = selected.id
    const timer = window.setInterval(() => {
      void loadThread(id, true)
    }, THREAD_POLL_MS)
    return () => window.clearInterval(timer)
  }, [selected, loadThread])

  useEffect(() => {
    if (selected || pendingPeer) return
    const timer = window.setInterval(() => {
      void loadList(inbox)
    }, INBOX_POLL_MS)
    return () => window.clearInterval(timer)
  }, [selected, pendingPeer, inbox, loadList])

  useEffect(() => {
    const refresh = () => {
      if (selected) void loadThread(selected.id, true)
      else void loadList(inbox)
    }
    window.addEventListener('focus', refresh)
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [selected, inbox, loadThread, loadList])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages, pendingPeer, selected])

  useEffect(() => {
    if (!attachment) {
      setAttachmentPreview(null)
      return
    }
    const url = URL.createObjectURL(attachment)
    setAttachmentPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [attachment])

  const clearAttachment = () => {
    setAttachment(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  useEffect(() => {
    if (!moderation?.suspendedUntil) return
    const timer = window.setInterval(() => setCountdownNow(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [moderation?.suspendedUntil])

  const peerId = selected?.peerId ?? pendingPeer?.peerId
  const peerName = selected?.peerName ?? pendingPeer?.peerName
  const peerColor = selected?.color ?? pendingPeer?.color ?? '#8ed9b7'

  const doSend = async (confirmPaid: boolean) => {
    if (!peerId || (!text.trim() && !attachment) || sending) return
    if (moderation && !moderation.canSend) {
      notify('Messaging is paused — see countdown below.')
      return
    }
    setSending(true)
    const sentText = text.trim()
    const optimisticId = newIdempotencyKey('opt')
    const optimisticMsg: ChatMessage = {
      id: optimisticId,
      sender_id: meId,
      body: sentText || (attachment ? '📷 Photo' : ''),
      created_at: new Date().toISOString(),
      hasAttachment: !!attachment,
    }
    if (selected?.id || pendingPeer) {
      setMessages((prev) => appendUniqueMessage(prev, optimisticMsg))
    }
    try {
      const res = await client.sendMessage({
        peerId,
        text: sentText,
        idempotencyKey: newIdempotencyKey('msg'),
        confirmPaid,
        file: attachment ?? undefined,
      })
      setText('')
      clearAttachment()
      setConfirmPay(null)
      setQuote(null)
      if (res.moderation) setModeration(res.moderation)

      const tab: InboxTab = selected?.inbox ?? (quote?.requiresPayment ? 'requests' : 'matches')
      const convoId = res.conversationId

      if (res.message) {
        const msg = res.message as ChatMessage
        setMessages((prev) => {
          const withoutOpt = prev.filter((m) => m.id !== optimisticId)
          return appendUniqueMessage(withoutOpt, msg)
        })
      } else if (convoId) {
        setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
        await loadThread(convoId, true)
      }

      if (convoId && selected?.id === convoId) {
        void loadList(tab)
      } else if (convoId && pendingPeer) {
        setPendingPeer(null)
        setSelected({
          id: convoId,
          peerId,
          peerName: peerName ?? 'Member',
          preview: res.blocked ? '⛔ Blocked message' : sentText,
          previewFromYou: true,
          time: 'now',
          unread: 0,
          color: peerColor,
          inbox: tab,
          kind: tab === 'requests' ? 'paid_intro' : 'match',
          isPaidIntro: tab === 'requests',
        })
        if (!res.message) await loadThread(convoId, true)
        void loadList(tab)
      } else if (convoId) {
        const list = await loadList(tab)
        const convo = list.find((c) => c.id === convoId)
        if (convo) {
          setSelected(convo)
          if (!res.message) await loadThread(convoId, true)
        }
      }

      if (res.blocked) {
        if (res.strikes?.justSuspended) {
          notify(`Messaging paused for 24 hours after 3 blocked messages in a row.`)
        }
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId))
      const err = e as Error & {
        status?: number
        body?: {
          quote?: MessageQuote
          reason?: string
          error?: string
          moderation?: MessageStrikeInfo
        }
      }
      if (err.status === 402 && err.body?.quote) {
        setConfirmPay({ credits: err.body.quote.credits })
        setQuote(err.body.quote)
      } else if (err.status === 403 && err.body?.error === 'message_suspended') {
        if (err.body.moderation) setModeration(err.body.moderation)
        notify(err.body.reason ?? err.message)
      } else {
        notify(err.message)
      }
    } finally {
      setSending(false)
    }
  }

  const messagingPaused = moderation != null && !moderation.canSend && !!moderation.suspendedUntil
  const strikeBanner =
    moderation && moderation.strikeStreak > 0
      ? strikeWarningCopy(moderation.strikeStreak, moderation.strikeLimit)
      : null

  const declineIntro = async () => {
    if (!selected || threadBusy) return
    setThreadBusy(true)
    try {
      await client.declineIntro(selected.id)
      notify('Introduction declined')
      setSelected(null)
      setConvMeta(null)
      setChatMenu(false)
      await loadList('requests')
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setThreadBusy(false)
    }
  }

  const unmatch = async () => {
    if (!peerId || threadBusy) return
    setThreadBusy(true)
    try {
      await client.unmatch(peerId)
      notify('Unmatched')
      setSelected(null)
      setPendingPeer(null)
      setConvMeta(null)
      setChatMenu(false)
      await loadList(inbox)
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setThreadBusy(false)
    }
  }

  const inThread = !!(selected || pendingPeer)

  useEffect(() => {
    onThreadActive?.(inThread)
    return () => onThreadActive?.(false)
  }, [inThread, onThreadActive])

  if (inThread) {
    return (
      <>
        <div className="chat-view">
          <header className="chat-head">
            <button
              className="button secondary chat-back"
              type="button"
              aria-label="Back to inbox"
              onClick={() => {
                setSelected(null)
                setPendingPeer(null)
                setChatMenu(false)
              }}
            >
              ← Back
            </button>
            <Avatar name={peerName ?? '?'} color={peerColor} size={38} />
            <div className="chat-head-title">
              <strong>{peerName}</strong>
              {convMeta?.isPaidIntro && convMeta.stakeStatus && (
                <small>Intro · {convMeta.stakeStatus.replace(/_/g, ' ')}</small>
              )}
            </div>
            <div className="chat-head-actions">
              {peerId && (
                <ReportBlock
                  reportedId={peerId}
                  targetType="user"
                  targetId={peerId}
                  notify={notify}
                  onDone={() => {
                    setSelected(null)
                    setPendingPeer(null)
                    void loadList(inbox)
                  }}
                />
              )}
              <button
                type="button"
                className="button secondary chat-menu-btn"
                aria-label="Conversation options"
                onClick={() => setChatMenu((v) => !v)}
              >
                ⋮
              </button>
            </div>
          </header>

          {chatMenu && (
            <div className="chat-menu">
              {convMeta?.canDeclineIntro && (
                <button type="button" disabled={threadBusy} onClick={() => void declineIntro()}>
                  Decline introduction
                </button>
              )}
              {selected && (
                <button type="button" disabled={threadBusy} onClick={() => void unmatch()}>
                  Unmatch
                </button>
              )}
            </div>
          )}

          {strikeBanner && (
            <div className={`chat-strike-banner${messagingPaused ? ' chat-strike-banner--paused' : ''}`}>
              {messagingPaused && moderation?.suspendedUntil ? (
                <>
                  <strong>Messaging paused</strong>
                  <span>
                    Time remaining: {formatSuspensionCountdown(moderation.suspendedUntil)}
                    {countdownNow ? '' : ''}
                  </span>
                  <small>Your last 3 messages in a row were blocked. Strikes reset after a successful message.</small>
                </>
              ) : (
                <>
                  <strong>{strikeBanner}</strong>
                  <small>Blocked messages appear in your chat but are not delivered.</small>
                </>
              )}
            </div>
          )}

          <div className="chat-scroll" ref={scrollRef}>
            {loadingThread && messages.length === 0 && (
              <div className="chat-loading">
                <div className="spinner" />
              </div>
            )}
            {!loadingThread && messages.length === 0 && (
              <p className="chat-empty-hint">
                {quote?.requiresPayment
                  ? `This introduction costs ${quote.credits} credits. Cost is shown before you send.`
                  : `Say hello to ${peerName}.`}
              </p>
            )}
            {(messages.length > 0 || !loadingThread) &&
              messages.map((m) => (
                <div
                  key={m.id}
                  className={`bubble ${m.sender_id === meId ? 'out' : 'in'}${m.blocked ? ' bubble--blocked' : ''}`}
                >
                  {m.hasAttachment && m.attachmentUrl && !m.blocked && (
                    <img
                      className="chat-attachment"
                      src={mediaUrl(m.attachmentUrl) ?? m.attachmentUrl}
                      alt=""
                      loading="lazy"
                    />
                  )}
                  {m.body ? <span>{m.body}</span> : null}
                  {m.blocked && m.blockReason && (
                    <div className="bubble-block-notice">
                      <strong>Not delivered</strong>
                      <p>{m.blockReason}</p>
                    </div>
                  )}
                  <div className="bubble-meta">
                    <time dateTime={m.created_at}>{formatMessageTime(m.created_at)}</time>
                    {m.sender_id === meId && !m.blocked && convMeta?.readReceiptsEnabled && (
                      <em className="read-receipt">{m.readByPeer ? 'Read' : 'Delivered'}</em>
                    )}
                    {m.sender_id === meId && m.blocked && (
                      <em className="read-receipt read-receipt--blocked">Blocked</em>
                    )}
                  </div>
                </div>
              ))}
          </div>
          {quote?.requiresPayment && (
            <div className="paid-note">
              Introduction: {quote.credits} credits
              {quote.dailyIntrosRemaining != null ? ` · ${quote.dailyIntrosRemaining} intros left today` : ''}
            </div>
          )}
          {convMeta?.kind === 'match' && peerId && (selected?.id ?? null) && (
            <PhotoRequestPanel
              meId={meId}
              peerId={peerId}
              conversationId={selected?.id ?? null}
              notify={notify}
              onSent={() => {
                if (selected?.id) void loadThread(selected.id, true)
              }}
            />
          )}
          {attachmentPreview && (
            <div className="composer-attachment">
              <img src={attachmentPreview} alt="Attachment preview" />
              <button type="button" className="button secondary" onClick={clearAttachment}>
                Remove
              </button>
            </div>
          )}
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault()
              void doSend(false)
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) setAttachment(file)
              }}
            />
            <button
              type="button"
              className="attach-btn"
              aria-label="Attach photo"
              onClick={() => fileInputRef.current?.click()}
            >
              📷
            </button>
            <input
              className="input"
              placeholder={messagingPaused ? 'Messaging paused…' : 'Write a message…'}
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={messagingPaused}
              autoComplete="off"
            />
            <button
              className="send-btn"
              type="submit"
              disabled={(!text.trim() && !attachment) || sending || messagingPaused}
            >
              →
            </button>
          </form>
        </div>

        {confirmPay && (
          <div className="modal-backdrop" onClick={() => setConfirmPay(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} role="dialog">
              <h2>Send introduction?</h2>
              <p>
                This costs <strong>{confirmPay.credits} credits</strong>. They can reply for free. Stakes are reserved
                until they respond or decline.
              </p>
              <div className="modal-actions">
                <button className="button primary" onClick={() => doSend(true)} disabled={sending}>
                  Confirm — {confirmPay.credits} credits
                </button>
                <button className="button secondary" onClick={() => setConfirmPay(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="panel-page messages-page">
      <div className="messages-heading">
        <div>
          <span className="eyebrow">Intentional messaging</span>
          <h1>Messages</h1>
        </div>
      </div>

      <div className="panel-tabs" role="tablist">
        <button className={inbox === 'matches' ? 'active' : ''} onClick={() => setInbox('matches')} role="tab" type="button">
          Inbox
        </button>
        <button className={inbox === 'requests' ? 'active' : ''} onClick={() => setInbox('requests')} role="tab" type="button">
          Requests
        </button>
      </div>

      {conversations && conversations.length > 0 && (
        <>
          <label className="mail-search">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              className="input"
              placeholder="Search messages"
              value={mailQuery}
              onChange={(e) => setMailQuery(e.target.value)}
              autoComplete="off"
            />
          </label>
          <label className="mail-unread-toggle">
            <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
            Show unread only
          </label>
        </>
      )}

      {conversations === null ? (
        <div className="loading-center">
          <div className="spinner" />
        </div>
      ) : filteredConversations.length === 0 ? (
        <div className="empty">
          <div>💬</div>
          <h2>{mailQuery || unreadOnly ? 'No matching messages' : inbox === 'matches' ? 'No matches yet' : 'No requests yet'}</h2>
          <p>
            {mailQuery || unreadOnly
              ? 'Try a different search or turn off unread-only.'
              : inbox === 'matches'
                ? 'Mutual interest opens free messaging here.'
                : 'Unmatched introductions from others appear here.'}
          </p>
        </div>
      ) : (
        <div className="thread-list mail-list">
          {filteredConversations.map((c) => {
            const metaLine = [c.peerAge != null ? String(c.peerAge) : null, c.peerCity].filter(Boolean).join(' · ')
            return (
              <button key={c.id} type="button" className="mail-row" onClick={() => openConversation(c)}>
                <div className="mail-avatar-wrap">
                  <Avatar name={c.peerName} color={c.color} size={52} className="mail-avatar" />
                </div>
                <span className="mail-body">
                  <span className="mail-top">
                    <strong>
                      {c.peerName}
                      {c.peerVerified && (
                        <span className="verified-badge verified-badge--inline" aria-label="Verified">
                          ✓
                        </span>
                      )}
                    </strong>
                    {c.time ? <time className="mail-time">{c.time}</time> : null}
                  </span>
                  {metaLine ? <span className="mail-meta">{metaLine}</span> : null}
                  <small className="thread-preview mail-preview">
                    {c.preview ? (
                      <>
                        <span className={`thread-preview-who${c.previewFromYou ? ' thread-preview-who--you' : ''}`}>
                          {c.previewFromYou ? 'You' : c.peerName}
                        </span>
                        <span className="thread-preview-body">{c.preview}</span>
                      </>
                    ) : (
                      c.inbox === 'requests' ? 'Introduction' : 'Say hello'
                    )}
                  </small>
                </span>
                {c.unread > 0 && <em className="unread-dot">{c.unread}</em>}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
